import type { AppLifecycle } from './engine';
import type { Entry, StoreChange, SyncCoordinator, SyncStorage } from './types';

// Browser adapters: IndexedDB for persistence, BroadcastChannel + Web Locks between tabs.

const ENTRIES = 'entries';
const META = 'meta';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function open(name: string): Promise<IDBDatabase> {
  const req = indexedDB.open(name, 1);
  req.onupgradeneeded = () => {
    const db = req.result;
    db.createObjectStore(ENTRIES, { keyPath: ['collection', 'record.id'] });
    db.createObjectStore(META);
  };
  return request(req);
}

/** One database per user, so signing out can drop all of it. */
export function indexedDbStorage(name: string): SyncStorage {
  let db: Promise<IDBDatabase> | null = null;
  const database = () => {
    db ??= open(name).then((opened) => {
      // Another tab is deleting or upgrading the database: step aside instead of blocking it.
      opened.onversionchange = () => {
        opened.close();
        db = null;
      };
      return opened;
    });
    return db;
  };

  return {
    async load() {
      const tx = (await database()).transaction([ENTRIES, META], 'readonly');
      const [entries, cursor] = await Promise.all([
        request(tx.objectStore(ENTRIES).getAll() as IDBRequest<Entry[]>),
        request(tx.objectStore(META).get('cursor') as IDBRequest<number | undefined>),
      ]);
      return { entries, cursor: cursor ?? 0 };
    },

    async write(change: StoreChange) {
      const tx = (await database()).transaction([ENTRIES, META], 'readwrite');
      const entries = tx.objectStore(ENTRIES);
      for (const key of change.remove) entries.delete([key.collection, key.id]);
      for (const entry of change.put) entries.put(entry);
      if (change.cursor !== undefined) tx.objectStore(META).put(change.cursor, 'cursor');
      await done(tx);
    },

    async clear() {
      if (db) (await db).close();
      db = null;
      await request(indexedDB.deleteDatabase(name));
    },

    close() {
      void db?.then((opened) => opened.close());
      db = null;
    },
  };
}

/** Keeps the tabs of the same user in step and lets only one of them sync at a time. */
export function browserCoordinator(name: string): SyncCoordinator {
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name);
  const listeners = new Set<(change: StoreChange) => void>();
  if (channel) {
    channel.onmessage = (event: MessageEvent<StoreChange>) => {
      for (const listener of listeners) listener(event.data);
    };
  }
  return {
    publish(change) {
      channel?.postMessage(change);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    exclusive(task) {
      const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
      return locks ? locks.request(name, task) : task();
    },
    close() {
      channel?.close();
      listeners.clear();
    },
  };
}

/** Syncs again when the connection returns or the tab becomes visible. */
export function browserLifecycle(): AppLifecycle {
  return {
    onResume(callback) {
      const onVisible = () => {
        if (document.visibilityState === 'visible') callback();
      };
      window.addEventListener('online', callback);
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        window.removeEventListener('online', callback);
        document.removeEventListener('visibilitychange', onVisible);
      };
    },
    isHidden: () => document.visibilityState === 'hidden',
    onActivity(callback) {
      // Mouse moves come by the hundred: one signal every few seconds is plenty.
      let last = 0;
      const onUse = () => {
        const now = Date.now();
        if (now - last < 5_000) return;
        last = now;
        callback();
      };
      const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
      for (const event of events) {
        window.addEventListener(event, onUse, { capture: true, passive: true });
      }
      return () => {
        for (const event of events) window.removeEventListener(event, onUse, { capture: true });
      };
    },
  };
}
